import type {
  BusinessModel,
  ContractModel,
  Corporate,
  MarkupBasis,
  MarkupKind,
  PAS,
  PmsSyncMode,
  WizardConfig,
} from "@/lib/api";

// No preselected defaults: every "selectable" field starts null and the
// respondent has to explicitly choose. Checkbox fields (apDest*, arDest*,
// shop overrides) default to unchecked.
export const INITIAL_CONFIG: WizardConfig = {
  businessModel: null,
  pas: null,
  corporate: null,
  corporateOther: "",
  apDestPas: false,
  apDestCorporate: false,
  arDestPas: false,
  arDestCorporate: false,
  pmsSyncMode: null,
  contractModel: null,
  markupKind: null,
  markupBasis: null,
  markupOther: "",
  markupValue: 15,
  shopRate: 65,
  shopOverrideTech: false,
  shopOverrideService: false,
  shopRateOverrideNotes: "",
  billRateMode: null,
  billRate: 95,
  apEmail: "",
  arEmail: "",
  usesCustomArBranding: null,
  arBrandingNotes: "",
  additionalNotes: "",
};

// --- Option lists with display labels ---------------------------------

export const BUSINESS_MODEL_OPTIONS: { value: BusinessModel; title: string; help: string }[] = [
  {
    value: "owned",
    title: "I own my units",
    help: "All properties are yours. We'll create AP bills only — no AR invoices to external owners.",
  },
  {
    value: "mixed",
    title: "Mixed portfolio",
    help: "Some owned, some managed for owners. AP for everyone; AR only on managed properties.",
  },
  {
    value: "managed",
    title: "I manage for investors / owners",
    help: "Every property has an external owner. AP and AR are both produced.",
  },
];

export const PAS_OPTIONS: PAS[] = [
  "AppFolio", "Buildium", "Yardi", "Rent Manager",
  "Rentvine", "Propertyware", "None",
];

export const CORPORATE_OPTIONS: Corporate[] = [
  "QuickBooks Online", "QuickBooks Desktop",
  "Xero", "Sage Intacct",
  "Bill.com", "AvidXchange", "Coupa",
  "Same as PAS", "Other",
];

/**
 * Allowed PMS sync modes per PAS — mirrors the constraints described
 * in the requirements:
 *   AppFolio    → line items attached to a bill
 *   Rent Manager / PropertyWare / Buildium / ResMan → bill object
 *   Rentvine    → bill object OR pre-bill
 *   Yardi       → PDF bucket
 *   None        → none (forced)
 * Every PAS supports "none" as an opt-out.
 */
export function syncOptionsFor(pas: PAS): PmsSyncMode[] {
  switch (pas) {
    case "AppFolio":     return ["bill-object", "line-items", "none"];
    case "Rent Manager": return ["bill-object", "none"];
    case "Propertyware": return ["bill-object", "none"];
    case "Rentvine":     return ["bill-object", "pre-bill", "none"];
    case "Yardi":        return ["pdf-bucket", "none"];
    case "Buildium":     return ["bill-object", "none"];
    case "None":         return ["none"];
  }
}

export const SYNC_LABEL: Record<PmsSyncMode, string> = {
  "line-items":  "Line items attached to a bill",
  "bill-object": "Single bill object",
  "pre-bill":    "Pre-bill state (your team finalizes)",
  "pdf-bucket":  "Bucket of PDFs",
  "none":        "Don't sync to PAS",
};

/**
 * Per-PAS label override. Currently used to mark AppFolio's `bill-object`
 * as the recommended choice, since AppFolio supports both line items and
 * bill objects but the latter is the better default.
 */
export function syncLabelFor(pas: PAS, mode: PmsSyncMode): string {
  if (pas === "AppFolio" && mode === "bill-object") {
    return "Single bill object (Recommended)";
  }
  return SYNC_LABEL[mode];
}

export const SYNC_HELP: Record<PmsSyncMode, string> = {
  "line-items":  "TrueCost pushes itemized billables tied to a bill — owner statements show line detail.",
  "bill-object": "TrueCost pushes one fully coded bill per work order; line items are bundled inside.",
  "pre-bill":    "TrueCost stages a pre-bill packet your accounting team codes and finalizes.",
  "pdf-bucket":  "TrueCost drops PDFs of the bill and supporting docs; no structured push.",
  "none":        "Records bills internally only. No system push.",
};

export const CONTRACT_MODEL_OPTIONS: { value: ContractModel; title: string; help: string }[] = [
  {
    value: "markup-internal-only",
    title: "I mark up internal costs only",
    help: "Apply markup to in-house tech labor; pass vendor invoices through at cost.",
  },
  {
    value: "markup-internal-and-invoices",
    title: "I mark up internal costs and invoices",
    help: "Apply markup to both in-house labor and vendor invoices.",
  },
  {
    value: "pass-through",
    title: "I do not mark up — pass through all costs",
    help: "Owners are billed exactly what was spent. No markup, no margin.",
  },
  {
    value: "flat-rate-per-owner",
    title: "Flat rate for maintenance — no per-meld bill",
    help: "Owners pay a flat maintenance fee; individual melds are not itemized to them.",
  },
];

export const MARKUP_KIND_OPTIONS: { value: MarkupKind; title: string; help: string }[] = [
  {
    value: "flat",
    title: "Flat fee",
    help: "A dollar amount added per meld.",
  },
  {
    value: "percentage",
    title: "Percentage",
    help: "A % of the total costs on the meld.",
  },
  {
    value: "other",
    title: "Other",
    help: "Anything else — tiered, custom rules, mixed schemes.",
  },
];

export const MARKUP_BASIS_OPTIONS: { value: MarkupBasis; title: string; help: string }[] = [
  {
    value: "universal",
    title: "Universal — same rate everywhere",
    help: "One rate applied across every meld.",
  },
  {
    value: "by-owner",
    title: "Varies by owner",
    help: "Different rates per owner. Configured per-owner post-survey.",
  },
  {
    value: "by-vendor",
    title: "Varies by vendor",
    help: "Different rates per vendor. Configured per-vendor post-survey.",
  },
  {
    value: "by-category",
    title: "Varies by work category",
    help: "Different rates per category (HVAC, plumbing, etc.).",
  },
];

// --- Derived helpers --------------------------------------------------

export function isUniversalFlat(cfg: WizardConfig): boolean {
  return cfg.markupKind === "flat" && cfg.markupBasis === "universal";
}

export function isUniversalPct(cfg: WizardConfig): boolean {
  return cfg.markupKind === "percentage" && cfg.markupBasis === "universal";
}

export function isVariableMarkup(cfg: WizardConfig): boolean {
  return cfg.markupKind !== null
      && cfg.markupKind !== "other"
      && cfg.markupBasis !== null
      && cfg.markupBasis !== "universal";
}

export function showsMarkup(cfg: WizardConfig): boolean {
  if (cfg.businessModel === "owned") return false;
  return cfg.contractModel === "markup-internal-only"
      || cfg.contractModel === "markup-internal-and-invoices";
}

export function effectiveMarkupPct(cfg: WizardConfig): number {
  // Real percentage when configured universally; otherwise a sample for
  // illustration. Used by the bill-rate "derived" preview and the sample
  // PDF math.
  if (showsMarkup(cfg) && isUniversalPct(cfg)) return cfg.markupValue;
  return 15;
}

export function effectiveBillRate(cfg: WizardConfig): number {
  if (cfg.billRateMode === "derived") {
    return cfg.shopRate * (1 + effectiveMarkupPct(cfg) / 100);
  }
  return cfg.billRate;
}

// --- Step graph --------------------------------------------------------

export type StepId =
  | "intro"
  | "business-model"
  | "systems"
  | "ap-ar-routing"
  | "pas-sync"
  | "contract-model"
  | "markup-application"
  | "shop-rate"
  | "bill-rate"
  | "ar-branding"
  | "additional-notes"
  | "preview"
  | "review";

export function activeSteps(cfg: WizardConfig): StepId[] {
  const steps: StepId[] = [
    "intro",
    "business-model",
    "systems",
    "ap-ar-routing",
    "pas-sync",
  ];
  if (cfg.businessModel !== "owned") {
    steps.push("contract-model");
    if (showsMarkup(cfg)) steps.push("markup-application");
  }
  steps.push("shop-rate", "bill-rate", "ar-branding", "additional-notes");
  steps.push("preview", "review");
  return steps;
}

/**
 * The corporate accounting label used when describing where AP/AR
 * lands. Returns null only for "Other" without a write-in, in which
 * case we can't name a specific system.
 */
export function corporateLabel(cfg: WizardConfig): string {
  if (cfg.corporate === null) return "Corporate accounting";
  if (cfg.corporate === "Other") return cfg.corporateOther.trim() || "Corporate accounting";
  if (cfg.corporate === "Same as PAS") {
    return cfg.pas && cfg.pas !== "None" ? cfg.pas : "Corporate accounting";
  }
  return cfg.corporate;
}

/**
 * Whether the chosen corporate system has email-based AP/AR
 * ingestion. Drives the email step's framing — "Other" falls back to
 * optional-export framing because we don't know what they're using.
 */
export function corporateUsesEmailIngestion(cfg: WizardConfig): boolean {
  if (cfg.corporate === null) return false;
  if (cfg.corporate === "Other") return false;
  if (cfg.corporate === "Same as PAS") return cfg.pas !== null && cfg.pas !== "None";
  return true;
}

/**
 * Per-step required-selection gate. Returns a short reason when the
 * step's required fields aren't filled in, or null when the user is
 * free to advance. The Wizard disables Continue and surfaces this
 * reason as a hint when non-null.
 */
export function stepBlockedReason(
  stepId: StepId,
  cfg: WizardConfig,
): string | null {
  switch (stepId) {
    case "business-model":
      return cfg.businessModel === null ? "Pick a business model to continue." : null;
    case "systems":
      if (cfg.pas === null) return "Pick your PAS to continue.";
      if (cfg.corporate === null) return "Pick your corporate accounting system to continue.";
      return null;
    case "pas-sync":
      // Nothing to pick when there's no PAS.
      if (cfg.pas === null || cfg.pas === "None") return null;
      return cfg.pmsSyncMode === null ? "Pick a sync mode to continue." : null;
    case "contract-model":
      return cfg.contractModel === null ? "Pick a contract model to continue." : null;
    case "markup-application":
      if (cfg.markupKind === null) return "Pick a markup kind to continue.";
      if (cfg.markupKind === "other") return null;
      return cfg.markupBasis === null ? "Pick how the markup is applied to continue." : null;
    case "bill-rate":
      return cfg.billRateMode === null ? "Pick how your bill rate is set to continue." : null;
    case "ar-branding":
      return cfg.usesCustomArBranding === null
        ? "Pick a branding option to continue."
        : null;
    default:
      return null;
  }
}

export const STEP_PHASE_LABEL: Record<StepId, string> = {
  "intro":              "Welcome",
  "business-model":     "Your business",
  "systems":            "Your systems",
  "ap-ar-routing":      "AP / AR routing",
  "pas-sync":           "PAS data sync",
  "contract-model":     "Your contract model",
  "markup-application": "How you mark up",
  "shop-rate":          "Your rates",
  "bill-rate":          "Your rates",
  "ar-branding":        "Accounts Receivable branding",
  "additional-notes":   "Anything else?",
  "preview":            "Sample work order",
  "review":             "Review & submit",
};
